import React, { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import assetsManager from "../assets/assetsmanager/assestsmanager";
import Pagination from "../component/Pagination";
import AlertModal from "../component/AlertModal";
import { deleteCacheDocs } from "../hooks/deleteCacheDocs";
import { AppContext } from "../context/context";

const Cache = () => {
  //static messages for modals
  const deleteAlertMessage = "Are you sure you want to delete?";

  //global states
  const { state, fetchCacheDocs } = useContext(AppContext);
  const { cacheTextQna, cacheMutualFund, cacheGreeting, cacheBingQna } = state;

  //modal states
  const [alertModalOpen, setAlertModalOpen] = useState(false);

  //pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  //table loading
  const [loading, setLoading] = useState(true);

  //table data
  const [tableData, setTableData] = useState([]);

  //data states
  const [searchInput, setSearchInput] = useState("");
  const [selectedDoc, setSelectedDoc] = useState(null);

  //active tab
  const [activeTab, setActiveTab] = useState(0);

  //tabs name
  const tabs = ["Website", "Mutual Fund", "Greeting", "Bing"];
  const routes = ["text_qna", "mutual_fund", "greeting", "bing_qna"];

  //handle change tab
  const handleTabChange = (i) => {
    setActiveTab(i);
  };

  //handle pagination
  const handleChangePage = (page) => {
    setPage(page);
  };

  //handle search input
  const handleChangeSearchInput = (e) => {
    setSearchInput(e.target.value);
    handleSearchData(e.target.value);
    setPage(0);
  };

  //handle search functionality
  const handleSearchData = async (text) => {
    let data = await getFormattedData();
    setLoading(false);
    let filteredData = data?.filter((item) => {
      return (
        item?.query?.toLowerCase()?.includes(text?.toLowerCase()) ||
        item?.response?.toLowerCase()?.includes(text?.toLowerCase())
      );
    });
    setTableData(filteredData);
  };

  //handle delete modal open
  const handleDeleteModal = async (item) => {
    setSelectedDoc(item);
    setAlertModalOpen(true);
  };

  //handle delete cache in modal
  const handleDeleteCacheYes = async () => {
    let cacheName = routes[activeTab];
    let docId = selectedDoc.id;
    try {
      toast.loading("Loading...");
      let data = await deleteCacheDocs(cacheName, docId, state?.activeTab);
      toast.dismiss();
      if (data.status === "Document deleted successfully!") {
        toast.success(data.status);
        await fetchCacheDocs(cacheName);
      } else {
        toast.error(data.status);
      }
    } catch (err) {
      console.log("Error: ", err);
      toast.dismiss();
      toast.error(err.status);
    }
    setAlertModalOpen(false);
    setPage(0);
  };

  //handle cancel delete cache data in modal
  const handleCacheModalNo = async (selectedDoc) => {
    setAlertModalOpen(false);
    setSelectedDoc(null);
  };

  //get active tab data in jsx required format
  const getFormattedData = () => {
    let arr =
      activeTab === 0
        ? cacheTextQna
        : activeTab === 1
        ? cacheMutualFund
        : activeTab === 2
        ? cacheGreeting
        : cacheBingQna;

    return arr?.map((item, i) => {
      return {
        id: item.id,
        query: item.query,
        ...(typeof item.response === "string"
          ? { response: item.response }
          : {
              response: item.response.answer ? item.response.answer : null,
              ctaResults: item.response.cta_results
                ? item.response.cta_results
                : null,
              recommendedQuestions: item.response.recommended_questions
                ? item.response.recommended_questions.join("\n")
                : null
            })
      };
    });
  };

  //refetching table data on tab change or cache data change
  useEffect(() => {
    if (!(cacheBingQna && cacheTextQna && cacheGreeting && cacheMutualFund)) {
      setLoading(true);
      return;
    }
    setTableData(getFormattedData);
    setPage(0);
  }, [cacheBingQna, cacheTextQna, cacheGreeting, cacheMutualFund, activeTab]);

  //handle scroll to top on tab chage or page change
  useEffect(() => {
    let id = document.getElementById("table-cont");
    id.scrollTop = 0;
  }, [page, activeTab]);

  //handle fetch cache data on page load
  useEffect(() => {
    routes.forEach((route) => fetchCacheDocs(route, state?.activeTab));
  }, []);

  return (
    <div className="h-[calc(100%_-_56px)] pb-2 mt-1.5">
      <div className="flex items-center justify-between">
        <div className="border-[1px] rounded-[4px] border-[#EAECF0] bg-white flex gap-4 p-1.5 mb-2">
          {tabs?.map((tab, i) => {
            return (
              <div
                key={i}
                onClick={() => handleTabChange(i)}
                className={`rounded-[4px] text-sm py-1.5 px-3.5 cursor-pointer text-center ${
                  activeTab === i
                    ? "bg-[#C7222A] text-white font-medium"
                    : "text-[#7C7C7C]"
                }`}
              >
                {tab}
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex flex-col gap-2 h-[calc(100%_-_51px)]">
        <div className="w-full relative">
          <img
            className="absolute top-1/2 transform translate-y-[-50%] left-[14px] h-[13px]"
            src={assetsManager.searchIcon}
            alt=""
          />
          <input
            className="w-[29rem] rounded-[4px] p-2 px-9 focus:outline-none bg-[#EFEFEF] text-sm"
            placeholder="Search Query"
            onChange={handleChangeSearchInput}
            value={searchInput}
          />
        </div>
        <div
          id="table-cont"
          className="h-[calc(100%_-_36px)] overflow-auto relative flex flex-col justify-between"
        >
          <table
            className={`w-full table-fixed ${
              tableData?.length === 0 && "sticky top-0"
            }`}
          >
            <thead className="sticky top-0">
              <tr>
                <th className="border-b-[2px] border-[#EAECF0] text-sm p-3 bg-[#F8F8F8] font-medium text-start">
                  <p>Query</p>
                </th>
                <th className="border-b-[2px] border-[#EAECF0] text-sm p-3 bg-[#F8F8F8] font-medium text-start">
                  Response
                </th>
                {(activeTab === 0 || activeTab === 3) && (
                  <>
                    <th className="border-b-[2px] border-[#EAECF0] text-sm p-3 bg-[#F8F8F8] font-medium text-start">
                      Recommended Questions
                    </th>
                    <th className="border-b-[2px] border-[#EAECF0] text-sm p-3 bg-[#F8F8F8] font-medium text-start">
                      CTA Links
                    </th>
                  </>
                )}
                <th className="border-b-[2px] border-[#EAECF0] text-sm p-3 bg-[#F8F8F8] font-medium text-start">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {tableData?.length > 0 &&
                tableData
                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item, i) => {
                    return (
                      <tr
                        key={item.id}
                        className="border-b-[2px] border-[#EAECF0]"
                      >
                        <td
                          title={item.query}
                          className="text-sm p-3 text-start"
                        >
                          <p className="ellipsis-text w-[90%] whitespace-pre-line">
                            {item?.query || "NA"}
                          </p>
                        </td>
                        <td
                          title={item.response}
                          className="text-sm p-3 text-start"
                        >
                          <p className="ellipsis-text w-[90%] whitespace-pre-line">
                            {item?.response || "NA"}
                          </p>
                        </td>
                        {(activeTab === 0 || activeTab === 3) && (
                          <>
                            <td
                              title={item?.recommendedQuestions}
                              className="text-sm p-3 text-start"
                            >
                              <p className="ellipsis-text w-[90%] whitespace-pre-line">
                                {item?.recommendedQuestions || "NA"}
                              </p>
                            </td>
                            <td
                              title={
                                item?.ctaResults?.length > 0
                                  ? item?.ctaResults.map((cta) => cta.cta_name)
                                  : "NA"
                              }
                              className="text-sm p-3 text-start"
                            >
                              <p className="ellipsis-text w-[90%] whitespace-pre-line">
                                {item?.ctaResults?.length > 0
                                  ? item?.ctaResults?.map((cta, i) => {
                                      return (
                                        <a
                                          key={i}
                                          href={cta?.cta_link}
                                          target="_blank"
                                          className="text-[#1C4EB2]"
                                          rel="noreferrer"
                                        >
                                          {cta?.cta_name}
                                          <br />
                                        </a>
                                      );
                                    })
                                  : "NA"}
                              </p>
                            </td>
                          </>
                        )}
                        <td className=" text-sm p-3 text-start">
                          <div className="flex gap-1 h-full">
                            <div
                              onClick={() => handleDeleteModal(item)}
                              className="flex items-center justify-center p-2 cursor-pointer h-full"
                            >
                              <img
                                className="h-[13px] w-[17px]"
                                src={assetsManager.deleteIcon}
                                alt=""
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
          {tableData?.length === 0 && (
            <div className=" h-[calc(100%_-_36px)] flex items-center justify-center">
              {loading ? (
                "loading..."
              ) : (
                <img
                  className="h-2/3 min-h-24 max-h-48"
                  src={assetsManager.emptyIllustration}
                  alt=""
                />
              )}
            </div>
          )}
        </div>
        <Pagination
          data={tableData}
          rowsPerPage={rowsPerPage}
          page={page}
          handleChangePage={handleChangePage}
        />
      </div>
      {alertModalOpen && (
        <AlertModal
          alertMessage={deleteAlertMessage}
          handleYes={handleDeleteCacheYes}
          handleNo={handleCacheModalNo}
        />
      )}
    </div>
  );
};

export default Cache;
